/* eslint-disable no-unused-vars */
import React from 'react';

import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import GalleryService from '@/services/gallery.service';
import LikeButton  from '@/shared-components/buttons/components/LikeButton';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BoardContentRenderer from '@/shared-components/forms/add-board-content/board-text-body/BoardContentRenderer';
import ThoughtIcon from '@/shared-components/icons/components/artwork/ThoughtIcon';
import ViewsIcon from '@/shared-components/icons/components/user-profile/ViewsIcon';
import Utils from '@/utils';
import { formatArticleDisplayDate } from '@/utils/articleDisplayDate';
import { assignLocalizedPublicPath } from '@/utils/localePublicUrl';
import {
  hydratePollsWithPersistedVotes,
  persistPollVote,
} from '@/utils/pollPersistence';
import {
  hydrateQuizzesWithPersistedAnswers,
  persistQuizAnswer,
} from '@/utils/quizPersistence';

import './ArticleInfo.css';

// ——— New: standalone header for articles ———
export function ArticleHeader({ fetchData }) {
  const t = useTranslations('common.content');
  const owner = fetchData.gallery || fetchData.artist || fetchData.collector;
  const createdDate = formatArticleDisplayDate(fetchData.createdAt);
  const updatedDate = formatArticleDisplayDate(fetchData.updatedAt);

  return (
    <div className="article__header">
      <UserProfileImage
        image={owner?.user_image?.image_id}
        typeOfProfile={owner?.user_type}
        className="article__header-avatar"
        onClick={() => assignLocalizedPublicPath(`/user/${owner?.user_username}`)}
      />

      <div className="article__header-center">
        <span className="article__header-username">
          {owner?.user_username}
        </span>
        {/* <span className="article__header-impressions">
          {Utils.numberParserMillionThousand(fetchData.post_visits)}
          <ViewsIcon className="article__header-impressions-icon" />
        </span> */}
      </div>
      <div className="article__header-dates">
        {createdDate && (
          <span className="article__header-date-created">
            {t('created', { date: createdDate })}
          </span>
        )}
        {fetchData.updatedAt !== fetchData.createdAt && updatedDate && (
          <span className="article__header-date-edited">
            {t('edited', { date: updatedDate })}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ArticleInfo({
  fetchData,
  likeCount,
  isLiked,
  onLike,
  onOpenComments,
  commentCount,
  initialSlideIndex = 0,
  isPreviewMode = false,
  isPublishedView = false,
}) {
  const privateActionHandler = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });
  const { stateHandler } = useStateHandler();

  // 1) Destructure your saved content blocks from the fetched data
  const {
    post_structure = [],
    post_texts     = [],
    post_pictures  = [],
    post_artworks  = [],
    post_polls     = [],      // { pollId, question, options:[{text,votes},…] }
    post_quizzes   = []
  } = fetchData.post_container || {};
  // 1) Lift polls into component state so we can update vote counts
  const [polls, setPolls] = React.useState(() =>
    hydratePollsWithPersistedVotes(fetchData._id, post_polls)
  );
  // 1.1) Lift quizzes into state so we can lock answers
  const [quizzes, setQuizzes] = React.useState(() =>
    hydrateQuizzesWithPersistedAnswers(fetchData._id, post_quizzes)
  );
  React.useEffect(() => {
    setPolls(hydratePollsWithPersistedVotes(fetchData._id, post_polls));
  }, [fetchData._id, post_polls]);
  React.useEffect(() => {
    setQuizzes(hydrateQuizzesWithPersistedAnswers(fetchData._id, post_quizzes));
  }, [fetchData._id, post_quizzes]);

  // --- Quiz result stats ---
  const total = quizzes.length;
  const answered = quizzes.filter(q => q.hasAnswered).length;
  const correct = quizzes.filter(
    q => q.hasAnswered && q.userAnswer === q.correctOption
  ).length;
  const pctTotal = total > 0 ? Math.round((correct / total) * 100) : 0;
  const pctAnswered = answered > 0
    ? Math.round((correct / answered) * 100)
    : 0;

  // 2) Build lookup maps for O(1) access by ID
  const textsById = React.useMemo(
    () => Object.fromEntries(post_texts.map(t => [t.textId, t])),
    [post_texts]
  );

  const picturesById = React.useMemo(
    () => Object.fromEntries(post_pictures.map(q => [q.groupId, q])),
    [post_pictures]
  );
  // new: group into arrays so each block.id maps to an array of artworks
  const artworksById = React.useMemo(
    () => Object.fromEntries(post_artworks.map(q => [q.groupId, q])),
    [post_artworks]
  );

  const pollsById = React.useMemo(
    () => Object.fromEntries(polls.map(p => [p.pollId, p])),
    [polls]
  );
  const quizzesById = React.useMemo(
    () => Object.fromEntries(quizzes.map(q => [q.quizId, q])),
    [quizzes]
  );

  // 3) Your vote‐casting handler
  const castVote = async (pollId, optionIndex) => {
    const updated = await GalleryService.castPollVote(
      fetchData._id,
      pollId,
      optionIndex
    );
    const withVote = {
      ...updated,
      hasVoted: true,
      userAnswer: optionIndex,
    };
    persistPollVote(fetchData._id, pollId, optionIndex);
    setPolls(ps => ps.map(p =>
      p.pollId === pollId ? withVote : p
    ));
    return withVote.options;
  };

  // 3.1) Quiz‐answer handler ———

  const castAnswer = async (quizId, answerIndex) => {
    await GalleryService.castQuizAnswer(
      fetchData._id,
      quizId,
      answerIndex
    );
    setQuizzes(qs =>
      qs.map(q =>
        q.quizId === quizId
          // record both that they've answered AND which answer they picked
          ? { ...q, hasAnswered: true, userAnswer: answerIndex }
          : q
      )
    );
    persistQuizAnswer(fetchData._id, quizId, answerIndex);
  };

  const [showCommentForm, setShowCommentForm] = React.useState(true);

  return (
    <>
      {/* ——— Main Content Loop ——— */}
      <BoardContentRenderer
        structure={post_structure}
        textsById={textsById}
        picturesById={picturesById}
        artworksById={artworksById}
        pollsById={pollsById}
        quizzesById={quizzesById}
        onVote={castVote}
        onAnswer={castAnswer}
        previewMode={isPreviewMode || isPublishedView}
        interactive={true}
      />
      {/* ——— Footer with comment toggle & like button ——— */}
      {!isPreviewMode &&
        (<div className="article__footer">
          {/* Quiz result summary (if 2+ quizzes) */}
          {total >= 2 && (
            <span
              className={
                `article__footer-quiz-result` +
                (answered === 0 ? ` unanswered` : ``)
              }
            >
              Quizzes Result: {correct}/{total} ({pctTotal}% / {pctAnswered}% avg)
            </span>
          )}
          <ThoughtIcon
            className="article__footer-comment-icon"
            onClick={(e) => {
              e.stopPropagation();
              if (commentCount > 0) {
                stateHandler.set('unregisteredUser', 'guest_article_comment_view');
              } else {
                stateHandler.set('unregisteredUser', 'guest_article_comment_add');
              }
              privateActionHandler(onOpenComments);
            }}
          />
          {/* 2) Then the count, if nonzero */}
          {commentCount > 0 && (
            <span className="article__footer-comment-count">
              {commentCount}
            </span>
          )}
          {/* 3) Finally, the Like button */}
          <LikeButton
            itemId={fetchData._id}
            typeOfItem="POST"
            isLiked={isLiked}
            onLikeAction={onLike}
            className="article__footer-like-button"
          />
          <span className="article__info-footer-like-count">
            {likeCount}
          </span>
        </div>)
      }
    </>
  );
}
